import React, { useState, useCallback } from 'react';
import { getPaper2McqSet } from '../paper2McqConfig';

/*
 * Paper2Quiz — a self-contained, client-marked multiple-choice player for the
 * Paper 2 "MCQs" group. Unlike the Theme A Quiz (which fetches questions and
 * checks answers on the server), these are practice questions marked in the
 * browser from paper2McqConfig, so there is no server round-trip and no XP.
 *
 * It reuses the Theme A quiz styles (ta-*) for a consistent look.
 *
 * Props:
 *   setId     — key into PAPER2_MCQ_SETS
 *   setTitle  — heading shown above the quiz
 *   onBack    — return to the dashboard
 */
export default function Paper2Quiz({ setId, setTitle, onBack }) {
  const set = getPaper2McqSet(setId);
  const questions = set?.questions || [];

  const [idx,      setIdx]      = useState(0);
  const [selected, setSelected] = useState(null);   // chosen option text
  const [answered, setAnswered] = useState(false);  // has the current question been checked
  const [score,    setScore]    = useState({ correct: 0, answered: 0 });

  const current  = questions[idx];
  const finished = questions.length > 0 && idx >= questions.length;

  const choose = useCallback((option) => {
    if (answered || !current) return;
    setSelected(option);
    setAnswered(true);
    setScore(s => ({
      correct:  s.correct + (option === current.answer ? 1 : 0),
      answered: s.answered + 1,
    }));
  }, [answered, current]);

  const next = useCallback(() => {
    setSelected(null);
    setAnswered(false);
    setIdx(i => i + 1);
  }, []);

  const restart = useCallback(() => {
    setIdx(0);
    setSelected(null);
    setAnswered(false);
    setScore({ correct: 0, answered: 0 });
  }, []);

  function optionClass(opt) {
    if (!answered) return 'ta-option';
    if (opt === current.answer) return 'ta-option ta-option-correct';
    if (opt === selected)       return 'ta-option ta-option-wrong';
    return 'ta-option';
  }

  const isCorrect = answered && selected === current?.answer;

  return (
    <div className="ta-canvas">
      <div className="ta-header">
        <button className="ta-back" onClick={onBack}>‹ Topics</button>
        <div>
          <h1 className="ta-title">Paper 2 · Multiple choice</h1>
          <p className="ta-subtitle">{setTitle || set?.title || 'Quiz'}</p>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="ta-empty">No questions for this selection.</div>
      ) : finished ? (
        <div className="ta-study">
          <div className="ta-summary">
            <div className="ta-summary-label">You scored</div>
            <div className="ta-summary-score">{score.correct} / {score.answered}</div>
            <div className="ta-summary-label">
              {Math.round((score.correct / Math.max(score.answered, 1)) * 100)}% correct
            </div>
            <div className="ta-controls" style={{ marginTop: 22 }}>
              <button className="ta-btn ta-btn-secondary" onClick={restart}>Try again</button>
              <button className="ta-btn ta-btn-primary" onClick={onBack}>Back to topics</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="ta-study">
          <div className="ta-progress">
            Question {idx + 1} / {questions.length} · Score {score.correct}/{score.answered}
          </div>

          <div className="ta-quiz-card">
            <p className="ta-question">{current.question}</p>

            {current.code && (
              <pre className="p2mcq-code"><code>{current.code}</code></pre>
            )}

            <div className="ta-options">
              {current.options.map((opt, i) => (
                <button
                  key={i}
                  className={optionClass(opt)}
                  onClick={() => choose(opt)}
                  disabled={answered}
                >
                  {opt}
                  {answered && opt === current.answer && <span className="ta-option-mark">✓</span>}
                  {answered && opt === selected && opt !== current.answer && <span className="ta-option-mark">✗</span>}
                </button>
              ))}
            </div>

            {answered && (
              <div className="ta-feedback">
                <span className={`ta-verdict ${isCorrect ? 'ta-verdict-correct' : 'ta-verdict-wrong'}`}>
                  {isCorrect ? 'Correct!' : 'Not quite.'}
                </span>
                <button className="ta-btn ta-btn-primary" onClick={next}>
                  {idx === questions.length - 1 ? 'See results' : 'Next question ›'}
                </button>
              </div>
            )}

            {answered && current.explanation && (
              <p className="p2mcq-explanation">{current.explanation}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
